import { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ClipboardCheck, Star, ChevronDown, ChevronRight, Pencil, Check, X } from 'lucide-react';

// ─── Editor Questionnaire (embedded IN the upload panel for V1) ───
export function FootageReportForm({ projectId, projectType, existingReport, onSubmitted }) {
  const isPhoto = projectType === 'photo';
  const [rating, setRating] = useState(existingReport?.raw?.footage_quality_rating || 0);
  const [coverageNotes, setCoverageNotes] = useState(existingReport?.raw?.coverage_notes || '');
  const [audioQuality, setAudioQuality] = useState(existingReport?.raw?.audio_quality || '');
  const [audioNotes, setAudioNotes] = useState(existingReport?.raw?.audio_notes || '');
  const [stability, setStability] = useState(existingReport?.raw?.footage_stability || '');
  const [generalNotes, setGeneralNotes] = useState(existingReport?.raw?.recommendations || '');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(!!existingReport);
  const [editing, setEditing] = useState(false);

  const submit = async () => {
    if (rating === 0) { toast.error('Please rate the footage quality'); return; }
    if (!audioQuality) { toast.error(isPhoto ? 'Please select exposure/lighting quality' : 'Please select audio quality'); return; }
    if (!stability) { toast.error(isPhoto ? 'Please select color consistency' : 'Please select footage stability'); return; }
    setSubmitting(true);
    try {
      await api.post(`/projects/${projectId}/footage-report`, {
        footage_quality_rating: rating, coverage_gaps: [], coverage_notes: coverageNotes,
        audio_quality: audioQuality, audio_notes: audioNotes,
        footage_stability: stability, expectation_risk: '', recommendations: generalNotes,
      });
      toast.success('Footage analysis submitted');
      setSubmitted(true);
      setEditing(false);
      if (onSubmitted) onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  const isLocked = submitted && !editing;

  return (
    <div className={`space-y-3 p-4 rounded-lg border ${isLocked ? 'bg-oxidized-teal/[0.03] border-oxidized-teal/20' : 'bg-ug-surface/50 border-gold-leaf/20'}`} data-testid="footage-report-form">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isLocked ? <Check className="w-4 h-4 text-oxidized-teal" /> : <ClipboardCheck className="w-4 h-4 text-ug-gold" />}
          <h3 className={`font-sans text-[11px] font-medium uppercase tracking-wider ${isLocked ? 'text-oxidized-teal/80' : 'text-ug-gold/80'}`}>
            {isLocked ? 'Footage Analysis Submitted' : isPhoto ? 'Photo Analysis' : 'Footage Analysis'}
          </h3>
        </div>
        {isLocked && (
          <button onClick={() => setEditing(true)} className="font-sans text-[9px] text-zinc-500 hover:text-ug-gold underline" data-testid="edit-footage-report">Edit</button>
        )}
      </div>
      {isLocked ? (
        <p className="text-[10px] text-oxidized-teal/60">Rating: {rating}/5 · {audioQuality} · {stability}</p>
      ) : (
        <>
          <p className="text-[11px] text-slate-500">Complete this before submitting. Your feedback helps us give the client a professional footage analysis.</p>

      {/* Rating — starts at 0 (no stars selected) */}
      <div className="space-y-1">
        <label className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500">{isPhoto ? 'Photo' : 'Footage'} Quality *</label>
        <div className="flex gap-1">
          {[1,2,3,4,5].map(s => (
            <button key={s} onClick={() => setRating(s)} type="button"
              className={`w-7 h-7 rounded-md border flex items-center justify-center transition-all ${s <= rating ? 'bg-ug-gold/20 border-ug-gold/40 text-ug-gold' : 'border-ug-border-strong text-slate-600'}`}>
              <Star className="w-3.5 h-3.5" fill={s <= rating ? 'currentColor' : 'none'} />
            </button>
          ))}
          {rating > 0 && <span className="text-[10px] text-slate-500 ml-1 self-center">{rating}/5</span>}
        </div>
      </div>

      {/* Coverage / Issues — free text */}
      <div className="space-y-1">
        <label className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500">{isPhoto ? 'Photo Issues' : 'Coverage Gaps'}</label>
        <p className="text-[9px] text-slate-600 italic">{isPhoto ? 'Note any issues — white balance, exposure, focus, color consistency, missing key moments, etc.' : 'Note any missing coverage — ceremony wide, vows audio, first dance, speeches, first look, getting ready, etc.'}</p>
        <textarea value={coverageNotes} onChange={e => setCoverageNotes(e.target.value)} rows={2} placeholder={isPhoto ? "Describe any issues with the photos..." : "Describe any gaps in coverage..."}
          className="w-full bg-ug-surface border border-ug-border text-zinc-100 rounded-md p-2 text-[12px] focus:border-ug-gold/40 outline-none resize-y" />
      </div>

      {/* Audio/Exposure + Stability/Color */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500">{isPhoto ? 'Exposure / Lighting *' : 'Audio Quality *'}</label>
          <Select value={audioQuality} onValueChange={setAudioQuality}>
            <SelectTrigger className="bg-ug-surface border-ug-border text-zinc-100 h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent className="bg-ug-raised border-ug-border-strong">
              {isPhoto ? (<>
                <SelectItem value="Well exposed" className="text-zinc-100 text-xs">Well exposed</SelectItem>
                <SelectItem value="Minor exposure issues" className="text-zinc-100 text-xs">Minor issues</SelectItem>
                <SelectItem value="Significant exposure problems" className="text-zinc-100 text-xs">Significant problems</SelectItem>
              </>) : (<>
                <SelectItem value="Clean" className="text-zinc-100 text-xs">Clean</SelectItem>
                <SelectItem value="Usable with cleanup" className="text-zinc-100 text-xs">Usable with cleanup</SelectItem>
                <SelectItem value="Significant issues" className="text-zinc-100 text-xs">Significant issues</SelectItem>
              </>)}
            </SelectContent>
          </Select>
          <Input value={audioNotes} onChange={e => setAudioNotes(e.target.value)} placeholder={isPhoto ? "Exposure notes..." : "Audio notes..."} className="bg-ug-surface border-ug-border text-zinc-100 h-7 text-[11px]" />
        </div>
        <div className="space-y-1">
          <label className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500">{isPhoto ? 'Color Consistency *' : 'Footage Stability *'}</label>
          <Select value={stability} onValueChange={setStability}>
            <SelectTrigger className="bg-ug-surface border-ug-border text-zinc-100 h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent className="bg-ug-raised border-ug-border-strong">
              {isPhoto ? (<>
                <SelectItem value="Consistent across cameras" className="text-zinc-100 text-xs">Consistent</SelectItem>
                <SelectItem value="Some color mismatches" className="text-zinc-100 text-xs">Some mismatches</SelectItem>
                <SelectItem value="Heavy correction needed" className="text-zinc-100 text-xs">Heavy correction needed</SelectItem>
              </>) : (<>
                <SelectItem value="Steady" className="text-zinc-100 text-xs">Steady</SelectItem>
                <SelectItem value="Some handheld shake" className="text-zinc-100 text-xs">Some handheld shake</SelectItem>
                <SelectItem value="Heavy stabilization needed" className="text-zinc-100 text-xs">Heavy stabilization needed</SelectItem>
              </>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* General Notes — replaces Expectation Risk + Recommendations */}
      <div className="space-y-1">
        <label className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500">Notes for the Videographer</label>
        <p className="text-[9px] text-slate-600 italic">Share any tips on how they could improve their shooting to make better films and make life easier for the editor.</p>
        <textarea value={generalNotes} onChange={e => setGeneralNotes(e.target.value)} rows={2} placeholder="General notes and recommendations..."
          className="w-full bg-ug-surface border border-ug-border text-zinc-100 rounded-md p-2 text-[12px] focus:border-ug-gold/40 outline-none resize-y" />
      </div>

      <Button onClick={submit} disabled={submitting || rating === 0 || !audioQuality || !stability}
        className="bg-ug-gold text-ug-deepest hover:bg-ug-gold-bright font-sans text-sm font-semibold h-10 w-full rounded-xl shadow-ug-gold disabled:opacity-30 disabled:shadow-none" data-testid="submit-footage-report">
        {submitting ? 'Analyzing with AI...' : 'Submit Footage Analysis'}
      </Button>
      {(rating === 0 || !audioQuality || !stability) && !submitting && (
        <p className="text-[9px] text-zinc-600 text-center mt-1">
          {rating === 0 ? 'Rate the footage quality above' : !audioQuality ? 'Select audio quality' : 'Select footage stability'}
        </p>
      )}
      </>
      )}
    </div>
  );
}

// ─── Admin/PM View (shows raw + polished, editable) ───
export function FootageReportAdmin({ report, projectId, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');

  if (!report || !report.raw) return null;

  const saveEdit = async () => {
    try {
      await api.put(`/projects/${projectId}/footage-report`, { ai_polished: editText });
      toast.success('Report updated');
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="rounded-lg border border-ug-border overflow-hidden" data-testid="footage-report-admin">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-3 hover:bg-ug-surface/50 transition-colors">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-3.5 h-3.5 text-ug-gold/60" />
          <span className="font-sans text-[10px] font-semibold uppercase tracking-wider text-zinc-100/80">Footage Analysis</span>
          {report.approved && <span className="text-[8px] font-mono bg-oxidized-teal/20 text-oxidized-teal px-1.5 py-0.5 rounded">Approved</span>}
          {!report.approved && <span className="text-[8px] font-mono bg-ug-gold/15 text-ug-gold px-1.5 py-0.5 rounded">Pending Review</span>}
        </div>
        {open ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
      </button>
      {open && (
        <div className="p-3 border-t border-ug-border-subtle space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-[9px] text-slate-500 font-mono">Quality:</span>
            <div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className="w-3 h-3" fill={s <= report.raw.footage_quality_rating ? '#D4C9A0' : 'none'} stroke={s <= report.raw.footage_quality_rating ? '#D4C9A0' : '#475569'} />)}</div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-ug-gold/60">Client-Facing Report</span>
              {!editing && <button onClick={() => { setEditing(true); setEditText(report.ai_polished); }} className="text-[9px] text-slate-500 hover:text-ug-gold font-mono flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>}
            </div>
            {editing ? (
              <div className="space-y-2">
                <textarea value={editText} onChange={e => setEditText(e.target.value)} rows={6} className="w-full bg-ug-surface border border-ug-border text-zinc-100 rounded-md p-2 text-[12px] focus:border-ug-gold/40 outline-none resize-y" />
                <div className="flex gap-2">
                  <button onClick={saveEdit} className="text-[9px] font-mono text-oxidized-teal flex items-center gap-1"><Check className="w-3 h-3" /> Save</button>
                  <button onClick={() => setEditing(false)} className="text-[9px] font-mono text-slate-500 flex items-center gap-1"><X className="w-3 h-3" /> Cancel</button>
                </div>
              </div>
            ) : (
              <div className="p-2 bg-ug-surface/50 rounded border border-ug-border-subtle text-[12px] text-zinc-100/80 whitespace-pre-wrap leading-relaxed">{report.ai_polished}</div>
            )}
          </div>
          <details className="text-[10px]">
            <summary className="font-sans text-[8px] font-medium uppercase tracking-wider text-slate-600 cursor-pointer">Raw Editor Feedback</summary>
            <div className="mt-2 space-y-1 text-slate-500">
              {report.raw.coverage_notes && <p>Coverage/Issues: {report.raw.coverage_notes}</p>}
              <p>Audio/Exposure: {report.raw.audio_quality}{report.raw.audio_notes ? ` — ${report.raw.audio_notes}` : ''}</p>
              <p>Stability/Color: {report.raw.footage_stability}</p>
              {report.raw.recommendations && <p>Notes: {report.raw.recommendations}</p>}
            </div>
          </details>
          {!report.approved && (
            <button onClick={async () => {
              try { await api.put(`/projects/${projectId}/footage-report/approve`); toast.success('Footage analysis approved — visible to client'); if (onUpdate) onUpdate(); } catch { toast.error('Failed'); }
            }} data-testid="approve-footage-report-btn"
              className="w-full p-2 rounded-md bg-oxidized-teal/10 border border-oxidized-teal/25 text-oxidized-teal font-sans text-[9px] font-semibold uppercase tracking-wider hover:bg-oxidized-teal/20 transition-colors flex items-center justify-center gap-1.5">
              <Check className="w-3 h-3" /> Approve Footage Analysis
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Client View (shows polished report only, after approval) ───
export function FootageReportClient({ report }) {
  const [open, setOpen] = useState(true);
  if (!report || !report.approved) return null;
  return (
    <div className="rounded-lg border border-gold-leaf/15 bg-gold-leaf/[0.02] overflow-hidden" data-testid="footage-report-client">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-3 hover:bg-gold-leaf/[0.03] transition-colors">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-ug-gold/70" />
          <span className="font-sans text-[11px] font-medium uppercase tracking-wider text-ug-gold/80">Footage Analysis</span>
        </div>
        {open ? <ChevronDown className="w-3.5 h-3.5 text-ug-gold/40" /> : <ChevronRight className="w-3.5 h-3.5 text-ug-gold/40" />}
      </button>
      {open && (
        <div className="p-4 border-t border-gold-leaf/10">
          <div className="text-[13px] text-zinc-100/80 whitespace-pre-wrap leading-relaxed break-words">{report.ai_polished}</div>
        </div>
      )}
    </div>
  );
}
